/**
 * ThemeToggle class manages state and functionality of the site's color theme
 *
 * @param {HTMLElement} el - The root element of the theme toggle menu
 * @returns {void}
 */
class ThemeToggle {
  constructor(el) {
    this.themeMenu = el;
    this.radioButtons = el.querySelectorAll('input[name=theme]');

    this.cookieName = 'wp-mitsoeThemeVal'; // "dark" or "light"
    this.themeOptions = {
      dark: 'theme-dark',
      light: 'theme-light',
    };

    this.selectedValue = this.getInitialValue(); // "dark" or "light"
    this.selectedThemeClass = this.themeOptions[this.selectedValue];

    this.setup();
  }

  /**
   * Initial setup:
   * (the initial theme is applied in PHP with the cookie value in ContextManager)
   * Checks the correct toggle,
   * and binds event listeners
   */
  setup() {
    this.setInitialToggleAsChecked();
    this.bindEvents();
  }

  /**
   * Add change event listeners to the radio buttons
   */
  bindEvents() {
    this.radioButtons.forEach((button) => {
      button.addEventListener('change', (e) => this.toggleTheme(e));
    });
  }

  /**
   * Set the radio button matching the selected theme as checked
   */
  setInitialToggleAsChecked() {
    const radioToCheck = this.themeMenu.querySelector(
      `input[name="theme"][value="${this.selectedValue}"]`,
    );
    if (radioToCheck) radioToCheck.checked = true;
  }

  /**
   * Get the theme from cookies if it exists,
   * otherwise return the dark theme
   *
   * @returns {string} - "dark" or "light"
   */
  getInitialValue() {
    const stored = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${this.cookieName}=`));
    const value = stored ? stored.split('=')[1] : null;
    return value && ['light', 'dark'].includes(value) ? value : 'dark';
  }

  /**
   * Update the selected theme based on user input
   *
   * @param {Event} e
   */
  toggleTheme(e) {
    const val = e.target.value; // "dark" or "light"
    if (val !== this.selectedValue) {
      this.selectedValue = val;
      this.selectedThemeClass = this.themeOptions[val];
      this.applyTheme(val);
      this.storeTheme(val);
    }
  }

  /**
   * Apply the theme to the <body> element only
   *
   * @param {string} val - "dark" or "light"
   */
  applyTheme(val) {
    document.body.classList.remove(
      this.themeOptions.dark,
      this.themeOptions.light,
    );
    document.body.classList.add(this.themeOptions[val]);
  }

  /**
   * Store the selected theme in a cookie
   *
   * @param {string} val - "dark" or "light"
   */
  storeTheme(val) {
    document.cookie = `${this.cookieName}=${val}; path=/; max-age=31536000; SameSite=Lax`; // 1 year
  }
}

export default ThemeToggle;
